import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JobAcceptPopupComponent } from '../job-accept-popup/job-accept-popup.component';
import { JobRejectPopupComponent } from '../job-reject-popup/job-reject-popup.component';
import { catchError, finalize, of, Subject, Subscriber, Subscription, tap } from 'rxjs';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { CompanyJobApprovePayload, CompanyJobRejectPayload } from '@edtech-ui/core/models/company-management/job-approval-approved-status';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { JobApproveDetails } from '@edtech-ui/core/models/company-management/get-job-by-id';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CandidateApiService } from '@edtech-ui/hiring-workspace/recruiter-dashboard/services/candidate-api.service';

@Component({
	selector: 'app-company-job-approval-detail-page',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule],
	templateUrl: './company-job-approval-detail-page.component.html',
	styleUrl: './company-job-approval-detail-page.component.scss'
})
export class CompanyJobApprovalDetailPageComponent implements OnInit, OnDestroy {


	jobDetails: JobApproveDetails | null = null;
	jobId!: string;
	subscription = new Subscription();
	isLoading = false;
	candidateSearchQuery = '';
	candidateTab: 'fresher' | 'experienced' | 'educator' = 'educator';
	Page = 1;
	totalPages = 1;
	totalCandidates = 0;
	hasNextPage = false;
	hasPreviousPage = false;
	private searchSubject = new Subject<string>();
	private lastSearchQuery = '';
	minDate: string = '';
	companyId: string = '';
	isSubmitDraftJob: boolean = false;
	isCandidateLoading = false;
	allCandidates: any[] = [];
	limit = 10;
	showingFrom = 0;
	showingTo = 0;


	origin = '';
	originPage = 1;

	constructor(
		private router: Router,
		private jobService: RecruitmentJobService,
		private toastr: ToastrService,
		private dialog: MatDialog,
		private route: ActivatedRoute,
		private candidateService: CandidateApiService
	) { }


	ngOnInit(): void {
		this.jobId = this.route.snapshot.paramMap.get('id') || '';
		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				this.origin = params['origin'] || '';
				this.originPage = Number(params['page']) || 1;
			})
		);
		if (this.jobId) {
			this.getAllPendingCompanyById();
		}
	}


	goTojobApprovalPage() {
		const queryParams = this.originPage > 1 ? { page: this.originPage } : {};
		if (this.origin === 'all-jobs') {
			this.router.navigate(['admin-student-platform/company-management/all-jobs'], { queryParams });
		} else {
			this.router.navigate(['admin-student-platform/company-management/job-approval'], { queryParams });
		}
	}


	getAllPendingCompanyById() {
		this.isLoading = true;
		this.subscription.add(
			this.jobService.getAllPendingCompanyById(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobDetails = response.Data;
						this.companyId = response.Data.company_id;
						//this.getAllCandidates(); will used later
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	postCompnayApproveJob(jobId: string) {

		const payload: CompanyJobApprovePayload = {
			note: 'job approved'
		}

		this.jobService.postCompnayApproveJob(jobId, payload).pipe(
			tap(response => {
				if (response.Success) {
					this.toastr.success(response.Data.message);
					this.getAllPendingCompanyById();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again');
				return of(null);
			}),
		).subscribe()
	}


	postCompnayRejectJob(jobId: string, rejectionReason: string) {

		const payload: CompanyJobRejectPayload = {
			rejection_reason: rejectionReason
		}

		this.jobService.postCompnayRejectJob(jobId, payload).pipe(
			tap(response => {
				if (response.Success) {
					this.toastr.success(response.Data.message);
					this.getAllPendingCompanyById();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again');
				return of(null);
			}),
		).subscribe()
	}


	openJobAcceptDialogBox(jobApproval: JobApproveDetails) {
		const dialogRef = this.dialog.open(JobAcceptPopupComponent, {
			data: {
				jobTitle: jobApproval.job_title,
				companyName: jobApproval.company_name
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.postCompnayApproveJob(jobApproval.job_id)
			}
		});
	}


	openJobRejectDialogBox(jobApproval: JobApproveDetails) {
		const dialogRef = this.dialog.open(JobRejectPopupComponent, {
			data: {
				jobTitle: jobApproval.job_title,
				companyName: jobApproval.company_name
			}
		});
		dialogRef.afterClosed().subscribe(reason => {
			if (reason) {
				this.postCompnayRejectJob(
					this.jobDetails!.job_id,
					reason
				)
			}
		});
	}


	getAssessmentPercentage(): number {
		const total = this.jobDetails?.stats?.applications || 0;
		const completed = this.jobDetails?.stats?.assessments_taken || 0;
		return total ? (completed / total) * 100 : 0;
	}


	getInterviewPercentage(): number {
		const total = this.jobDetails?.stats?.applications || 0;
		const completed = this.jobDetails?.stats?.interviews_taken || 0;
		return total ? (completed / total) * 100 : 0;
	}


	getShortlistedPercentage(): number {
		const total = this.jobDetails?.stats?.applications || 0;
		const completed = this.jobDetails?.stats?.shortlisted || 0;
		return total ? (completed / total) * 100 : 0;
	}


	getAllCandidates(): void {
		if (this.candidateSearchQuery !== this.lastSearchQuery) {
			this.Page = 1;
			this.lastSearchQuery = this.candidateSearchQuery;
		}
		

		this.isCandidateLoading = true;

		const typeMap: Record<string, string> = {
			Fresher: 'fresher',
			Experienced: 'experienced',
			Educator: 'educator'
		};

		const payload = {
			company_id: this.companyId,
			candidate_type: this.candidateTab,
			job_id: this.jobId || undefined,
			search: this.candidateSearchQuery || undefined,
			page: this.Page,
			limit: this.limit
		}

		this.subscription.add(
			this.candidateService.listCandidates(payload).pipe(
				finalize(() => {
					this.isCandidateLoading = false;
				})
			).subscribe({
				next: (response) => {
					if (response.Success) {
						this.allCandidates = response.Data.candidates;
						this.totalCandidates = response.Data.total;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
						this.showingFrom = this.totalCandidates === 0 ? 0 : ((this.Page - 1) * this.limit) + 1;
						this.showingTo = Math.min(this.Page * this.limit, this.totalCandidates);
					} else {
						this.toastr.error(response.Message);
					}
				},
				error: () => {
					this.isCandidateLoading = false;
				}
			}));
	}


	onSearchChange(): void {
		this.searchSubject.next(this.candidateSearchQuery);
	}


	clearFilters(): void {
		this.candidateSearchQuery = '';
		this.getAllCandidates();
	}


	nextPage(): void {
		if (this.Page < this.totalPages) {
			this.Page++;
			this.getAllCandidates();
		}
	}

	
	prevPage(): void {
		if (this.Page > 1) {
			this.Page--;
			this.getAllCandidates();
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
